import React, { useCallback, useMemo, useState } from 'react';
import { ValidationError } from 'yup';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  SelectChangeEvent,
  Stack,
  styled,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import { InputSelect } from 'app/components/elements/InputSelect';
import { NumericInput } from 'app/components/elements/NumericInput';
import { NotificationSettingsSchema } from 'utils/validate';
import {
  DEFAULT_SETTINGS,
  DAYS_OF_WEEK,
  REMINDER_UNITS,
  TUserOption,
  TNotificationSettings,
  useInjection
} from './hook';

// ─── Types ────────────────────────────────────────────────────────────────────

type TFormState = Omit<TNotificationSettings, 'defaultNotificationWindow' | 'reminderFrequency'> & {
  defaultNotificationWindow: number | undefined;
  reminderFrequency: {
    value: number | undefined;
    unit: 'HOURS' | 'DAYS';
  };
};

type TFormErrors = Record<string, string>;

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionLabel = ({ label, hint }: { label: string; hint: string }) => (
  <Stack gap={0.75}>
    <Stack direction="row" alignItems="center" gap={1}>
      <LabelText>{label}</LabelText>
      <Tooltip title={hint} arrow placement="top">
        <InfoOutlinedIcon sx={{ fontSize: 18, color: '#6E7C91', cursor: 'help' }} />
      </Tooltip>
    </Stack>
    <HintText>{hint}</HintText>
  </Stack>
);

const TimeInput = ({
  value,
  onChange,
  width = 248,
  inputError
}: {
  value: string;
  onChange: (value: string) => void;
  width?: number | string;
  inputError?: string;
}) => (
  <Stack gap={0.5}>
    <TextField
      type="time"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      size="small"
      inputProps={{ step: 300 }}
      error={!!inputError}
      sx={{
        width,
        maxWidth: '100%',
        '& .MuiOutlinedInput-root': {
          height: 52,
          borderRadius: '8px',
          backgroundColor: '#FFFFFF',
          fontSize: 16,
          color: '#111827',
          '& fieldset': { borderColor: inputError ? '#D32F2F' : '#D4DAE4' },
          '&:hover fieldset': { borderColor: inputError ? '#D32F2F' : '#AEB8C7' },
          '&.Mui-focused fieldset': {
            borderColor: inputError ? '#D32F2F' : '#1769C2',
            borderWidth: 1
          }
        },
        '& input::-webkit-calendar-picker-indicator': {
          opacity: 0,
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          cursor: 'pointer'
        }
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <AccessTimeOutlinedIcon sx={{ fontSize: 21, color: '#728197' }} />
          </InputAdornment>
        )
      }}
    />
    {inputError && <FieldErrorText>{inputError}</FieldErrorText>}
  </Stack>
);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmailRecipientsInput = ({
  value,
  onChange,
  inputError,
  userOptions = []
}: {
  value: string[];
  onChange: (value: string[]) => void;
  inputError?: string;
  userOptions?: TUserOption[];
}) => {
  const [inputValue, setInputValue] = useState('');
  const [typingError, setTypingError] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return [];
    return userOptions
      .filter(
        (u) =>
          (u.email.toLowerCase().includes(q) || u.name.toLowerCase().includes(q)) &&
          !value.some((e) => e.toLowerCase() === u.email.toLowerCase()) // ← was !value.includes(u.email)
      )
      .slice(0, 8);
  }, [inputValue, userOptions, value]);

  const addEmail = useCallback(
    (raw: string) => {
      const email = raw.trim().toLowerCase();
      if (!email) return;

      if (!EMAIL_REGEX.test(email)) {
        setTypingError('Invalid email address');
        return;
      }
      if (value.some((e) => e.toLowerCase() === email)) {
        // ← was .map().includes()
        setTypingError('Email already added');
        return;
      }

      onChange([...value, email]);
      setInputValue('');
      setTypingError('');
      setDropdownOpen(false);
    },
    [onChange, value]
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addEmail(inputValue);
    }
  };

  const handleSelect = (email: string) => {
    addEmail(email);
  };

  const activeError = typingError || inputError;

  return (
    <Stack gap={0.5} sx={{ position: 'relative' }}>
      <ChipInputBox hasError={!!activeError}>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} flex={1} minWidth={0}>
          {value.map((email) => (
            <Chip
              key={email}
              label={email}
              onDelete={() => onChange(value.filter((item) => item !== email))}
              size="small"
              sx={{
                height: 36,
                borderRadius: '6px',
                border: '1px solid #AFCDF4',
                backgroundColor: '#EAF3FF',
                color: '#0757B5',
                fontSize: 14,
                fontWeight: 500,
                '& .MuiChip-label': { px: 1.25 },
                '& .MuiChip-deleteIcon': {
                  color: '#1769C2',
                  fontSize: 18,
                  '&:hover': { color: '#0C4D99' }
                }
              }}
            />
          ))}

          <EmailInput
            value={inputValue}
            onChange={(event) => {
              setInputValue(event.target.value);
              setDropdownOpen(true);
              if (typingError) setTypingError('');
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              setTimeout(() => {
                setDropdownOpen(false);
                if (inputValue.trim()) addEmail(inputValue);
              }, 150);
            }}
            onFocus={() => {
              if (inputValue.trim()) setDropdownOpen(true);
            }}
            placeholder="Enter email address and press Enter to add"
            aria-label="Add email recipient"
          />
        </Stack>

        <KeyboardArrowDownIcon sx={{ color: '#7A8799', fontSize: 23, flexShrink: 0 }} />
      </ChipInputBox>

      {/* ── Suggestion Dropdown ── */}

      {dropdownOpen && filtered.length > 0 && (
        <SuggestionDropdown>
          {filtered.map((u) => {
            const q = inputValue.trim().toLowerCase();
            const emailLower = u.email.toLowerCase();
            const idx = emailLower.indexOf(q);
            return (
              <SuggestionItem
                key={u.email}
                onMouseDown={(e) => {
                  e.preventDefault(); // prevent blur before click
                  handleSelect(u.email);
                }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                  {/* Email with highlighted match */}
                  <Typography component="span" sx={{ fontSize: 14, color: '#111827' }}>
                    {idx >= 0 ? (
                      <>
                        {u.email.slice(0, idx)}
                        <strong style={{ color: '#1769C2' }}>
                          {u.email.slice(idx, idx + q.length)}
                        </strong>
                        {u.email.slice(idx + q.length)}
                      </>
                    ) : (
                      u.email
                    )}
                  </Typography>

                  {/* Name badge */}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 12,
                      color: '#64748B',
                      backgroundColor: '#F1F5F9',
                      borderRadius: '4px',
                      px: 0.75,
                      py: 0.25,
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}>
                    {u.name}
                  </Typography>
                </Stack>
              </SuggestionItem>
            );
          })}
        </SuggestionDropdown>
      )}
      {activeError && <FieldErrorText>{activeError}</FieldErrorText>}
    </Stack>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const NotificationSettingsPage = () => {
  const { settings, isLoading, userOptions, isSaving, handleSaveSettings } = useInjection();
  const [form, setForm] = React.useState<TFormState>(DEFAULT_SETTINGS);
  const [formErrors, setFormErrors] = React.useState<TFormErrors>({});

  React.useEffect(() => {
    setForm(settings);
  }, [settings]);

  const update = useCallback(<K extends keyof TFormState>(key: K, value: TFormState[K]) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    setFormErrors((previous) => {
      const next = { ...previous };
      Object.keys(next).forEach((k) => {
        if (k === key || k.startsWith(`${key}.`)) delete next[k];
      });
      return next;
    });
  }, []);

  const getError = (path: string) => formErrors[path] || '';

  const onSave = useCallback(async () => {
    try {
      await NotificationSettingsSchema.validate(form, { abortEarly: false });
    } catch (err) {
      if (err instanceof ValidationError) {
        const errors: TFormErrors = {};
        err.inner.forEach((e) => {
          if (e.path && !errors[e.path]) {
            errors[e.path] = e.message;
          }
        });
        setFormErrors(errors);
      }
      return;
    }

    setFormErrors({});
    const payload: TNotificationSettings = {
      ...form,
      defaultNotificationWindow: form.defaultNotificationWindow!,
      reminderFrequency: {
        ...form.reminderFrequency,
        value: form.reminderFrequency.value!
      }
    };
    handleSaveSettings(payload);
  }, [form, handleSaveSettings]);

  if (isLoading) {
    return (
      <WrapperPage>
        <Stack alignItems="center" justifyContent="center" minHeight="60vh">
          <CircularProgress />
        </Stack>
      </WrapperPage>
    );
  }

  return (
    <WrapperPage>
      <HeaderRow>
        <Stack gap={0.75}>
          <PageTitle>Notification Settings</PageTitle>
          <PageSubtitle>
            Configure default notification preferences, recipients and report schedules.
          </PageSubtitle>
        </Stack>

        <ButtonCustom
          label={isSaving ? 'Saving...' : 'Save Changes'}
          disabled={isSaving}
          onClick={onSave}
          style={{
            width: 'auto',
            minWidth: 168,
            height: 46,
            paddingLeft: 22,
            paddingRight: 22,
            borderRadius: 7
          }}
        />
      </HeaderRow>

      <SettingsCard>
        {/* ── Default Notification Window ── */}
        <SettingsRow>
          <Grid container alignItems="center" columnSpacing={{ xs: 2, md: 5 }} rowSpacing={2}>
            <Grid item xs={12} md={5}>
              <SectionLabel
                label="Default Notification Window"
                hint="Send notification this many hours before maintenance is due."
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Stack sx={{ maxWidth: 480 }}>
                <Stack direction="row" alignItems="flex-start">
                  <NumericFieldBox sx={{ flex: 1 }}>
                    <NumericInput
                      value={form.defaultNotificationWindow}
                      onChange={(value) => update('defaultNotificationWindow', value)}
                      placeholder="0"
                      inputError={getError('defaultNotificationWindow')}
                    />
                  </NumericFieldBox>
                  <SuffixBox>Hours before due</SuffixBox>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </SettingsRow>

        <Divider />

        {/* ── Email Recipients ── */}
        <SettingsRow>
          <Grid container alignItems="flex-start" columnSpacing={{ xs: 2, md: 5 }} rowSpacing={2}>
            <Grid item xs={12} md={5}>
              <SectionLabel
                label="Email Recipients"
                hint="Add email addresses that will receive maintenance notifications and reports."
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <EmailRecipientsInput
                value={form.emailRecipients}
                onChange={(value) => update('emailRecipients', value)}
                inputError={getError('emailRecipients')}
                userOptions={userOptions}
              />
            </Grid>
          </Grid>
        </SettingsRow>

        <Divider />

        {/* ── Daily Report Schedule ── */}
        <SettingsRow>
          <Grid container alignItems="center" columnSpacing={{ xs: 2, md: 5 }} rowSpacing={2}>
            <Grid item xs={12} md={5}>
              <SectionLabel
                label="Daily Report Schedule"
                hint="Configure time for sending daily maintenance reports."
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <TimeInput
                value={form.dailyReportSchedule.time}
                onChange={(time) =>
                  update('dailyReportSchedule', { ...form.dailyReportSchedule, time })
                }
                inputError={getError('dailyReportSchedule.time')}
              />
            </Grid>
          </Grid>
        </SettingsRow>

        <Divider />

        {/* ── Weekly Report Schedule ── */}
        <SettingsRow>
          <Grid container alignItems="center" columnSpacing={{ xs: 2, md: 5 }} rowSpacing={2}>
            <Grid item xs={12} md={5}>
              <SectionLabel
                label="Weekly Report Schedule"
                hint="Configure day and time for sending weekly maintenance reports."
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                gap={2}
                alignItems="center"
                sx={{ maxWidth: 480 }}>
                <SelectFieldBox>
                  <InputSelect
                    options={DAYS_OF_WEEK}
                    value={form.weeklyReportSchedule.dayOfWeek}
                    placeholder="Select day"
                    inputError={getError('weeklyReportSchedule.dayOfWeek')}
                    onChange={(event: SelectChangeEvent<unknown>) =>
                      update('weeklyReportSchedule', {
                        ...form.weeklyReportSchedule,
                        dayOfWeek: event.target.value as string
                      })
                    }
                  />
                </SelectFieldBox>

                <TimeInput
                  width={208}
                  value={form.weeklyReportSchedule.time}
                  onChange={(time) =>
                    update('weeklyReportSchedule', { ...form.weeklyReportSchedule, time })
                  }
                  inputError={getError('weeklyReportSchedule.time')}
                />
              </Stack>
            </Grid>
          </Grid>
        </SettingsRow>

        <Divider />

        {/* ── Reminder Frequency ── */}
        <SettingsRow>
          <Grid container alignItems="center" columnSpacing={{ xs: 2, md: 5 }} rowSpacing={2}>
            <Grid item xs={12} md={5}>
              <SectionLabel
                label="Reminder Frequency"
                hint="Define how often reminder notifications are sent until the maintenance activity is completed."
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                gap={2}
                alignItems="flex-start"
                sx={{ maxWidth: 480 }}>
                <Stack flex={1}>
                  <NumericFieldBoxStandalone sx={{ width: '100%' }}>
                    <NumericInput
                      value={form.reminderFrequency.value}
                      onChange={(value) =>
                        update('reminderFrequency', { ...form.reminderFrequency, value })
                      }
                      placeholder="0"
                      inputError={getError('reminderFrequency.value')}
                    />
                  </NumericFieldBoxStandalone>
                  <FieldCaption>Value</FieldCaption>
                </Stack>

                <Stack flex={0.82}>
                  <SelectFieldBox sx={{ width: '100%' }}>
                    <InputSelect
                      options={REMINDER_UNITS}
                      value={form.reminderFrequency.unit}
                      placeholder="Select unit"
                      inputError={getError('reminderFrequency.unit')}
                      onChange={(event: SelectChangeEvent<unknown>) =>
                        update('reminderFrequency', {
                          ...form.reminderFrequency,
                          unit: event.target.value as 'HOURS' | 'DAYS'
                        })
                      }
                    />
                  </SelectFieldBox>
                  <FieldCaption>Unit</FieldCaption>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </SettingsRow>
      </SettingsCard>

      <InfoBox>
        <InfoOutlinedIcon sx={{ color: '#0866C6', fontSize: 30, mt: 0.25, flexShrink: 0 }} />
        <Stack gap={0.5}>
          <InfoTitle>How it works</InfoTitle>
          <InfoText>
            A notification will be sent when maintenance is due within the Default Notification
            Window.
          </InfoText>
          <InfoText>
            If not completed, reminder notifications will be sent every Reminder Frequency until the
            maintenance activity is completed.
          </InfoText>
        </Stack>
      </InfoBox>
    </WrapperPage>
  );
};

// ─── Styled Components ────────────────────────────────────────────────────────

const WrapperPage = styled(Stack)(({ theme }) => ({
  width: '100%',
  minHeight: '100vh',
  padding: '28px 40px 48px',
  backgroundColor: '#FFFFFF',
  [theme.breakpoints.down('md')]: {
    padding: '24px 20px 36px'
  }
}));

const HeaderRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 24,
  marginBottom: 26,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    '& > button': { width: '100% !important' }
  }
}));

const PageTitle = styled(Typography)(() => ({
  color: '#071E42',
  fontSize: 26,
  lineHeight: 1.25,
  fontWeight: 700
}));

const PageSubtitle = styled(Typography)(() => ({
  color: '#53627A',
  fontSize: 15,
  lineHeight: 1.6
}));

const SettingsCard = styled(Stack)(() => ({
  overflow: 'hidden',
  border: '1px solid #D9DEE7',
  borderRadius: 9,
  backgroundColor: '#FFFFFF',
  boxShadow: '0 1px 2px rgba(16, 24, 40, 0.02)',
  '& .MuiDivider-root': { borderColor: '#E4E8EE' }
}));

const SettingsRow = styled(Box)(({ theme }) => ({
  padding: '30px 26px',
  [theme.breakpoints.down('sm')]: {
    padding: '24px 18px'
  }
}));

const LabelText = styled(Typography)(() => ({
  color: '#071E42',
  fontSize: 16,
  lineHeight: 1.4,
  fontWeight: 700
}));

const HintText = styled(Typography)(() => ({
  maxWidth: 430,
  color: '#53627A',
  fontSize: 14,
  lineHeight: 1.65
}));

const FieldCaption = styled(Typography)(() => ({
  paddingLeft: 3,
  color: '#64748B',
  fontSize: 13,
  lineHeight: 1.4
}));

const FieldErrorText = styled(Typography)(() => ({
  color: '#D32F2F',
  fontSize: 13,
  lineHeight: 1.4,
  paddingLeft: 2
}));

const NumericFieldBox = styled(Box)(() => ({
  width: 248,
  maxWidth: '100%',
  '& .MuiFormControl-root': { width: '100%' },
  '& .MuiOutlinedInput-root': {
    width: '100%',
    height: 52,
    borderRadius: '8px 0 0 8px',
    backgroundColor: '#FFFFFF',
    fontSize: 16,
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#AEB8C7' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1769C2', borderWidth: 1 }
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#D4DAE4',
    borderRight: 'none',
    borderRadius: '8px 0 0 8px'
  },
  '& input': {
    paddingLeft: 16,
    textAlign: 'left'
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    fontSize: 12
  }
}));

const NumericFieldBoxStandalone = styled(Box)(() => ({
  width: 248,
  maxWidth: '100%',
  '& .MuiFormControl-root': { width: '100%' },
  '& .MuiOutlinedInput-root': {
    width: '100%',
    height: 52,
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    fontSize: 16,
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#AEB8C7' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1769C2', borderWidth: 1 }
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#D4DAE4',
    borderRadius: '8px'
  },
  '& input': {
    paddingLeft: 16,
    textAlign: 'left'
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    fontSize: 12
  }
}));

const SuffixBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 52,
  padding: '0 18px',
  border: '1px solid #D4DAE4',
  borderLeft: 'none',
  borderTopRightRadius: 8,
  borderBottomRightRadius: 8,
  backgroundColor: '#F3F4F6',
  color: '#354258',
  fontSize: 15,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box'
}));

const SelectFieldBox = styled(Box)(() => ({
  width: 248,
  maxWidth: '100%',
  '& .MuiFormControl-root': { width: '100%' },
  '& .MuiOutlinedInput-root': {
    height: 52,
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    fontSize: 15,
    '& fieldset': { borderColor: '#D4DAE4' },
    '&:hover fieldset': { borderColor: '#AEB8C7' },
    '&.Mui-focused fieldset': { borderColor: '#1769C2', borderWidth: 1 }
  }
}));

const ChipInputBox = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'hasError'
})<{ hasError?: boolean }>(({ hasError }) => ({
  width: '100%',
  maxWidth: 575,
  minHeight: 96,
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 10,
  padding: '10px 14px',
  border: `1px solid ${hasError ? '#D32F2F' : '#D4DAE4'}`,
  borderRadius: 8,
  backgroundColor: '#FFFFFF',
  '&:focus-within': {
    borderColor: hasError ? '#D32F2F' : '#1769C2',
    boxShadow: `0 0 0 1px ${hasError ? '#D32F2F' : '#1769C2'}`
  }
}));

const EmailInput = styled('input')(() => ({
  flex: 1,
  minWidth: 240,
  height: 34,
  padding: '0 2px',
  border: 0,
  outline: 0,
  backgroundColor: 'transparent',
  color: '#111827',
  fontFamily: 'inherit',
  fontSize: 14,
  '&::placeholder': { color: '#8793A5', opacity: 1 }
}));

const InfoBox = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 18,
  marginTop: 26,
  padding: '20px 24px',
  border: '1px solid #B8D5FA',
  borderRadius: 8,
  backgroundColor: '#F4F8FF',
  [theme.breakpoints.down('sm')]: {
    padding: '18px'
  }
}));

const InfoTitle = styled(Typography)(() => ({
  color: '#0757B5',
  fontSize: 16,
  fontWeight: 700
}));

const InfoText = styled(Typography)(() => ({
  color: '#26364F',
  fontSize: 14,
  lineHeight: 1.55
}));

const SuggestionDropdown = styled(Stack)(() => ({
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  zIndex: 1300,
  maxHeight: 240,
  overflowY: 'auto',
  border: '1px solid #D4DAE4',
  borderRadius: 8,
  backgroundColor: '#FFFFFF',
  boxShadow: '0 4px 12px rgba(16, 24, 40, 0.10)',
  padding: '4px 0'
}));

const SuggestionItem = styled(Box)(() => ({
  padding: '10px 14px',
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': {
    backgroundColor: '#F0F6FF'
  }
}));
